"use client";

import { useEffect, useMemo, useState } from "react";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Field,
  FieldContent,
  FieldGroup,
  FieldLabel,
  FieldDescription,
} from "@/components/ui/field";

import {
  AlertCircleIcon,
  Calendar1Icon,
  FileTextIcon,
  InfoIcon,
} from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";
import { Kvps } from "@/lib/kvps";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import { Skeleton } from "@/components/ui/skeleton";
import { useLemaSalesPersonsQuery } from "@/lib/queries";
import type { GetLemaSalesPersonsResponse } from "@/app/api/lema-sales-persons/route";
import { getLemaSalesPersonsRequestSchema } from "@/lib/schemas/cat/lema-sales-persons-request";
import type { z } from "zod";

interface AdditionalInfoProps {
  onCanProceedChange?: (canProceed: boolean) => void;
  currentKvps?: Kvps | null;
}

type LemaSellerRow = GetLemaSalesPersonsResponse["lemaSalesPersons"][number];

function formatLemaSellerLabel(
  row: Pick<LemaSellerRow, "firstname" | "lastname" | "lemaId">,
) {
  const first = (row.firstname ?? "").trim();
  const last = (row.lastname ?? "").trim();
  const lema = (row.lemaId ?? "").trim();
  const fullName = `${first} ${last}`.trim();
  return `${fullName} (${lema})`;
}

type SellerQueryParams = Partial<
  z.infer<typeof getLemaSalesPersonsRequestSchema>
>;

export function RetailAdditionalInformationsStep({
  onCanProceedChange,
  currentKvps,
}: AdditionalInfoProps) {
  const [signatureDate, setSignatureDate] = useState("");
  const [preferredDeliveryDate, setPreferredDeliveryDate] = useState("");
  const [maxDeliveryDate, setMaxDeliveryDate] = useState("");
  const [firstDayOfProductionWeek] = useState(""); // format "23/2026"
  const [salesPerson, setSalesPerson] = useState<LemaSellerRow | null>(null);
  const [isDemoVehicle] = useState(false);
  const [partnerOffersRefusal, setPartnerOffersRefusal] = useState(false);
  const [vgfOffersConsent, setVgfOffersConsent] = useState(false);

  // TODO: Re-enable filtering by wholesalerNumber + brandLetter when step 1
  // calls the real vehicle API and returns the vehicle-linked KVPS.
  const sellerQueryParams = useMemo((): SellerQueryParams => {
    // Filtering is intentionally disabled for now.
    void currentKvps;
    return {};
  }, [currentKvps]);

  const sellerFilterActive = Boolean(
    sellerQueryParams.wholesalerNumber ?? sellerQueryParams.brandLetter,
  );

  const lemaSalesPersonsQuery = useLemaSalesPersonsQuery(
    sellerQueryParams,
    undefined,
  );

  const sellerOptions = useMemo(
    () => lemaSalesPersonsQuery.data?.lemaSalesPersons ?? [],
    [lemaSalesPersonsQuery.data?.lemaSalesPersons],
  );

  const showSellerQueryError = lemaSalesPersonsQuery.isError;

  /** Empty table or API returned no rows (avoid relying only on `total`, which may be absent on error payloads). */
  const showSellerEmptyList =
    lemaSalesPersonsQuery.isSuccess && sellerOptions.length === 0;

  const emptySellerMessageKvps =
    "Aucun vendeur disponible pour ce KVPS. Contactez le service de référentiel LEMA pour ajouter ou mettre à jour les vendeurs dans l’annuaire.";

  const emptySellerMessageRepositoryEmpty =
    "Aucun vendeur n’est présent dans l’annuaire applicatif. Contactez les services techniques de l’application.";

  const totalInDatabase = lemaSalesPersonsQuery.data?.totalInDatabase;

  const emptySellerAlertMessage =
    totalInDatabase === undefined
      ? sellerFilterActive
        ? emptySellerMessageKvps
        : emptySellerMessageRepositoryEmpty
      : totalInDatabase === 0
        ? emptySellerMessageRepositoryEmpty
        : sellerFilterActive
          ? emptySellerMessageKvps
          : emptySellerMessageRepositoryEmpty;

  useEffect(() => {
    onCanProceedChange?.(
      !!signatureDate && !!preferredDeliveryDate && !!salesPerson,
    );
  }, [signatureDate, preferredDeliveryDate, salesPerson, onCanProceedChange]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileTextIcon className="h-5 w-5 text-[#005EA8]" />
            <span>Informations Complémentaires</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Dates */}
          <div className="space-y-4">
            <Label className="flex items-center space-x-2 text-base font-semibold">
              <span>Dates importantes</span>
            </Label>

            <FieldGroup className="grid grid-cols-3 gap-4 md:grid-cols-3">
              <Field>
                <FieldLabel htmlFor="signatureDate">
                  Date du bon de commande *
                </FieldLabel>
                <DatePicker
                  id="signatureDate"
                  value={signatureDate ? new Date(signatureDate) : undefined}
                  onChange={(e) => {
                    setSignatureDate(e?.toString() || "");
                    setPreferredDeliveryDate("");
                    setMaxDeliveryDate("");
                  }}
                  max={new Date()}
                  disabled={isDemoVehicle}
                />
                <FieldDescription className="text-sm text-gray-500">
                  Date de signature du BC par le client
                </FieldDescription>
              </Field>

              <Field>
                <FieldLabel htmlFor="preferredDeliveryDate">
                  Date livraison souhaitée *
                </FieldLabel>
                <DatePicker
                  id="preferredDeliveryDate"
                  value={
                    preferredDeliveryDate
                      ? new Date(preferredDeliveryDate)
                      : undefined
                  }
                  onChange={(e) => {
                    setPreferredDeliveryDate(e?.toString() || "");
                    setMaxDeliveryDate("");
                  }}
                  min={
                    firstDayOfProductionWeek
                      ? new Date(firstDayOfProductionWeek)
                      : new Date(signatureDate)
                  }
                  disabled={isDemoVehicle || !signatureDate}
                />
                <FieldDescription className="text-sm text-gray-500">
                  Date souhaitée par le client
                </FieldDescription>
              </Field>

              <Field>
                <FieldLabel htmlFor="maxDeliveryDate">
                  Date livraison extrême *
                </FieldLabel>
                <DatePicker
                  id="maxDeliveryDate"
                  value={
                    maxDeliveryDate ? new Date(maxDeliveryDate) : undefined
                  }
                  onChange={(e) => setMaxDeliveryDate(e?.toString() || "")}
                  min={new Date(preferredDeliveryDate)}
                  disabled={isDemoVehicle || !preferredDeliveryDate}
                />
                <FieldDescription className="text-sm text-gray-500">
                  Date limite acceptable
                </FieldDescription>
              </Field>
            </FieldGroup>

            <Alert>
              <InfoIcon className="h-4 w-4" />
              <AlertDescription>
                {
                  "Ces dates permettent de planifier la livraison et d'anticiper les contraintes logistiques."
                }
              </AlertDescription>
            </Alert>
          </div>

          {/* Consent */}
          <div className="space-y-4 border-t pt-4">
            <Label className="flex items-center space-x-2 text-base font-semibold">
              <span>Consentements</span>
            </Label>

            <FieldGroup className="flex gap-4">
              <Field orientation="horizontal">
                <Checkbox
                  id="vgfOffersConsent"
                  checked={vgfOffersConsent === true}
                  onCheckedChange={(checked) =>
                    setVgfOffersConsent(checked === true)
                  }
                />
                <FieldLabel htmlFor="vgfOffersConsent">
                  {
                    "J’accepte de recevoir des offres commerciales, y compris des offres personnalisées, relatives aux produits et services commercialisés par Volkswagen Group France, Volkswagen Bank Gmbh, et Volkswagen Financial Services S.A."
                  }
                </FieldLabel>
              </Field>

              <Field orientation="horizontal">
                <Checkbox
                  id="partnerOffersRefusal"
                  checked={partnerOffersRefusal === true}
                  onCheckedChange={(checked) =>
                    setPartnerOffersRefusal(checked === true)
                  }
                />
                <FieldLabel htmlFor="partnerOffersRefusal">
                  Je m’oppose à recevoir des offres commerciales relatives aux
                  produits et services commercialisés par l’établissement
                  vendeur.
                </FieldLabel>
              </Field>
            </FieldGroup>
          </div>

          {/* Seller Identification */}
          <div className="space-y-4 border-t pt-4">
            <Label className="text-base font-semibold">
              Identification du vendeur (LEMA)
            </Label>

            <Field>
              <FieldLabel htmlFor="salesPerson">Vendeur *</FieldLabel>
              <FieldContent>
                {lemaSalesPersonsQuery.isPending ? (
                  <Skeleton className="h-9 w-full max-w-md" />
                ) : showSellerQueryError ? (
                  <Alert variant="destructive">
                    <AlertCircleIcon className="h-4 w-4" />
                    <AlertDescription>
                      Une erreur est survenue lors du chargement des vendeurs
                      LEMA. Veuillez réessayer plus tard.
                    </AlertDescription>
                  </Alert>
                ) : showSellerEmptyList ? (
                  <Alert variant="destructive">
                    <AlertCircleIcon className="h-4 w-4" />
                    <AlertDescription>
                      {emptySellerAlertMessage}
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Combobox
                    items={sellerOptions}
                    itemToStringLabel={(row) => formatLemaSellerLabel(row)}
                    value={salesPerson}
                    onValueChange={(next) => setSalesPerson(next)}
                  >
                    <ComboboxInput
                      id="salesPerson"
                      placeholder="Rechercher un vendeur…"
                      showClear={salesPerson !== null}
                      aria-label="Rechercher un vendeur LEMA"
                      disabled={sellerOptions.length === 0}
                      className="max-w-md"
                    />
                    <ComboboxContent>
                      <ComboboxEmpty>Aucun vendeur trouvé.</ComboboxEmpty>
                      <ComboboxList>
                        {(row) => (
                          <ComboboxItem key={row.id} value={row}>
                            {formatLemaSellerLabel(row)}
                          </ComboboxItem>
                        )}
                      </ComboboxList>
                    </ComboboxContent>
                  </Combobox>
                )}
              </FieldContent>
              <FieldDescription className="text-sm text-gray-500">
                Identifiant LEMA du vendeur responsable de cette transaction
              </FieldDescription>
            </Field>

            <Alert className="border-blue-200 bg-blue-50">
              <InfoIcon className="h-4 w-4" />
              <AlertDescription>
                Le code LEMA permet de lier le vendeur au dossier pour le suivi
                des commissions et statistiques de vente.
              </AlertDescription>
            </Alert>
          </div>

          {/* Lead time circular */}
          <div className="space-y-2 border-t pt-4">
            <Label className="flex items-center gap-2 text-base font-semibold">
              Circulaire des délais
            </Label>

            <Alert>
              <InfoIcon className="h-4 w-4" />
              <AlertDescription>
                Pensez à vérifier la circulaire des délais
              </AlertDescription>
            </Alert>

            <div className="px-4 py-3">
              <p className="text-sm font-medium">
                Semaine de production souhaitée :
              </p>

              {/* TODO: replace value with MuleSoft data of the actual week of production */}
              <p className="flex items-center gap-2 text-2xl font-bold tracking-tight">
                <Calendar1Icon />
                48/2025
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
